import React, { useEffect, useState } from "react";
import { CustomIcon } from "../shared/CustomIcon";
import { apiVisitorJourney, apiTemporarilyVisible } from "../followuptree/api";

// Iter-54 (D): Renders two things when applicable on a member profile:
//   1) Deceased "Make Temporarily Visible" control (Super Admin only)
//   2) Visitor Journey — legacy follow-up log entries from the visitor
//      phase, preserved after a Visitor is promoted to a full Member.
// Silent when neither applies.
export default function MemberProfileExtras({ member, currentUser, onFlash }) {
  const [journey, setJourney] = useState(null);
  const [busyVis, setBusyVis] = useState(false);
  const [visDays, setVisDays] = useState(30);
  const [visUntil, setVisUntil] = useState(member?.temporaryVisibilityUntil || null);

  const role = currentUser?.role;
  const isSuperAdmin = role === "superadmin";
  const isAdmin = isSuperAdmin || role === "limited_admin";
  const status = member?.memberStatus || "";
  const isDeceased = status === "Deceased";

  useEffect(() => {
    setVisUntil(member?.temporaryVisibilityUntil || null);
  }, [member?.id, member?.temporaryVisibilityUntil]);

  // Fetch visitor journey silently for admins (endpoint returns 403 for others).
  useEffect(() => {
    let cancelled = false;
    if (!isAdmin || !member?.id) { setJourney(null); return; }
    apiVisitorJourney(member.id)
      .then(r => { if (!cancelled) setJourney(r); })
      .catch(() => { if (!cancelled) setJourney(null); });
    return () => { cancelled = true; };
  }, [isAdmin, member?.id]);

  const enableTempVisibility = async () => {
    const days = Math.max(1, Math.min(365, Number(visDays) || 30));
    setBusyVis(true);
    try {
      const res = await apiTemporarilyVisible(member.id, days);
      setVisUntil(res?.temporaryVisibilityUntil || null);
      onFlash && onFlash(`Profile temporarily visible for ${res?.days || days} day(s).`, "success");
    } catch (e) {
      onFlash && onFlash(e?.message || "Failed to enable temporary visibility.", "error");
    } finally {
      setBusyVis(false);
    }
  };

  const showDeceased = isSuperAdmin && isDeceased;
  const journeyItems = journey?.items || [];
  const showJourney = isAdmin && journeyItems.length > 0;

  if (!showDeceased && !showJourney) return null;

  return (
    <div data-testid="member-profile-extras" style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 14 }}>
      {showDeceased && (
        <div
          data-testid="deceased-visibility-card"
          className="card"
          style={{ padding: 18, borderLeft: "3px solid rgba(160,160,220,0.65)" }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
            <CustomIcon name="lock" size={16} />
            <div style={{ fontFamily: "'Cinzel',serif", fontSize: 13, color: "#a5b4fc", textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700 }}>
              Deceased — Temporary Visibility
            </div>
          </div>
          <div style={{ fontSize: 12, color: "var(--kc-sub)", lineHeight: 1.5, marginBottom: 10 }}>
            Deceased members are hidden from non-admin viewers. As Super Admin you may unhide this profile for a limited number of days (e.g. for a memorial service).
          </div>
          {visUntil && (
            <div data-testid="deceased-visibility-until" style={{ fontSize: 12, color: "var(--kc-accent)", marginBottom: 10 }}>
              Currently visible until <strong>{new Date(visUntil).toLocaleString()}</strong>.
            </div>
          )}
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <label style={{ fontSize: 11, color: "var(--kc-sub)", textTransform: "uppercase", letterSpacing: "0.08em" }}>Days:</label>
            <input
              type="number"
              min={1}
              max={365}
              value={visDays}
              onChange={e => setVisDays(e.target.value)}
              data-testid="deceased-visibility-days"
              className="form-input"
              style={{ width: 80 }}
            />
            <button
              type="button"
              className="btn btn-sm btn-outline"
              data-testid="deceased-visibility-btn"
              onClick={enableTempVisibility}
              disabled={busyVis}
            >
              {busyVis ? "Applying…" : (visUntil ? "Extend Visibility" : "Make Temporarily Visible")}
            </button>
          </div>
        </div>
      )}

      {showJourney && (
        <div
          data-testid="visitor-journey-card"
          className="card"
          style={{ padding: 18, borderLeft: "3px solid rgba(255,138,101,0.65)" }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
            <CustomIcon name="person" size={16} />
            <div style={{ fontFamily: "'Cinzel',serif", fontSize: 13, color: "#ff8a65", textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700 }}>
              Visitor Journey
            </div>
            <span style={{ fontSize: 10, color: "var(--kc-sub)" }}>
              ({journeyItems.length} entr{journeyItems.length === 1 ? "y" : "ies"})
            </span>
          </div>
          <div style={{ fontSize: 12, color: "var(--kc-sub)", lineHeight: 1.5, marginBottom: 12 }}>
            Preserved follow-up history from before this person was placed in the Follow-Up tree. First contact:{" "}
            <strong style={{ color: "var(--kc-accent)" }}>{journey?.firstFollowUpDate || "—"}</strong>.
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 320, overflowY: "auto" }}>
            {journeyItems.map(e => (
              <div
                key={e.id}
                data-testid={`visitor-journey-entry-${e.id}`}
                style={{ padding: "8px 10px", background: "#111", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 8 }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "baseline", flexWrap: "wrap" }}>
                  <strong style={{ fontSize: 12, color: "#FFF" }}>{e.date}</strong>
                  <span style={{ fontSize: 11, color: "var(--kc-sub)" }}>{e.method || "—"}</span>
                </div>
                {e.notes && (
                  <div style={{ fontSize: 12, color: "var(--kc-sub)", marginTop: 4, whiteSpace: "pre-wrap" }}>
                    {e.notes}
                  </div>
                )}
                <div style={{ fontSize: 10, color: "var(--kc-meta)", marginTop: 4 }}>
                  by {e.loggedByName || "Someone"}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
